import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../constants';

/**
 * Create fresh state each time (avoids stale Date.now())
 */
const getInitialState = () => ({
  ExamID: 1,
  ExamDate: Date.now(),
  ExamName: '',
  ExamPassingGradeThreshold: 80,
  ExamAttempt: 1,
  ExamGrade: 0,
  ExamTimeAllowed: 90,
  ExamDateBarrier: 7,
  examquestions: [],
  sections: [],
  error: null,
  loading: false,
  examPopulated: false,
});

/**
 * Async thunk with proper error handling
 */
export const fetchExamById = createAsyncThunk(
  'exams/fetchExamById',
  async (examId, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/getall/${examId}`);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e.response?.data || e.message
      );
    }
  }
);

/**
 * Fisher-Yates shuffle (efficient + safe)
 */
function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const examSlice = createSlice({
  name: 'exam',
  initialState : getInitialState(),
  reducers: {
    setSelectedAnswerForQuestion: (state, action) => {
      const questionToUpdate = state.examquestions.find(
        q => q.ExamQuestionID === action.payload.ExamQuestionID
      );

      const index = state.examquestions.indexOf(questionToUpdate);

      state.examquestions[index] = {
        ...state.examquestions[index],
        selectedAnswer: action.payload,
      };
    },

    /**
     * Reset exam safely
     */
    clearExam: () => getInitialState(),
  },

  extraReducers: builder => {
    builder
      .addCase(fetchExamById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExamById.fulfilled, (state, action) => {
        const data = action.payload;

        state.loading = false;
        state.error = null;

        state.ExamName = data.ExamName;
        state.ExamID = Number(data.ExamID);
        state.ExamPassingGradeThreshhold = parseFloat(
          data.ExamPassingGradeThreshold
        );
        state.ExamTimeAllowed =
          Number(data.ExamTimeAllowed);
        state.ExamDateBarrier = data.ExamDateBarrier;

        state.sections = data.sections;
        state.examquestions = shuffleArray(data.examquestions);

        state.examPopulated = true;
      })
      .addCase(fetchExamById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const {
  setSelectedAnswerForQuestion,
  clearExam,
} = examSlice.actions;

export default examSlice.reducer;